import React, { useState, useEffect, useRef } from "react";
import { Box, Container, Typography } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DP, HeroSection, OutlineButton, pulse, morphShape } from "./HomeStyles";
import { keyframes } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";

const textReveal = keyframes`
  from { opacity: 0; transform: translateY(40px) skewY(2deg); filter: blur(4px); }
  to   { opacity: 1; transform: translateY(0)    skewY(0deg); filter: blur(0);   }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const scaleReveal = keyframes`
  from { opacity: 0; transform: scale(1.06); }
  to   { opacity: 1; transform: scale(1.04); }
`;
const shimmerSlide = keyframes`
  0%   { transform: translateX(-100%) skewX(-15deg); }
  100% { transform: translateX(300%)  skewX(-15deg); }
`;
const particleFloat = keyframes`
  0%   { transform: translateY(0px)   translateX(0px)   rotate(0deg);   opacity: 0.4; }
  33%  { transform: translateY(-26px) translateX(12px)  rotate(120deg); opacity: 0.65;}
  66%  { transform: translateY(-13px) translateX(-9px)  rotate(240deg); opacity: 0.45;}
  100% { transform: translateY(0px)   translateX(0px)   rotate(360deg); opacity: 0.4; }
`;
const borderGlow = keyframes`
  0%   { box-shadow: 0 0 0 0px rgba(21,101,192,0.6), inset 0 0 0 2px #1565C0; }
  50%  { box-shadow: 0 0 20px 8px rgba(21,101,192,0.3), inset 0 0 0 2px #42A5F5; }
  100% { box-shadow: 0 0 0 0px rgba(21,101,192,0), inset 0 0 0 2px #1565C0; }
`;

const AnimatedBorderButton = React.forwardRef<HTMLDivElement, any>(({ children, ...props }, ref) => (
  <Box ref={ref} sx={{ borderRadius: "50px", animation: `${borderGlow} 3s ease-in-out infinite`, transition: "all 0.3s ease", "&:hover": { boxShadow: `0 0 24px 12px rgba(21,101,192,0.4), inset 0 0 0 2px #1565C0`, transform: "translateY(-3px)" } }} {...props}>
    {children}
  </Box>
));

const statValues = ["50+", "5K+", "200+"];

const particles = [
  { size: 5, top: "14%", left: "5%",  delay: "0s",   dur: "6s"   },
  { size: 4, top: "68%", left: "9%",  delay: "1.5s", dur: "8s"   },
  { size: 6, top: "38%", left: "2%",  delay: "3s",   dur: "7s"   },
  { size: 5, top: "82%", left: "16%", delay: "0.8s", dur: "9s"   },
  { size: 5, top: "22%", left: "88%", delay: "2s",   dur: "6.5s" },
  { size: 4, top: "58%", left: "84%", delay: "4s",   dur: "8.5s" },
  { size: 7, top: "76%", left: "91%", delay: "1s",   dur: "7.5s" },
  { size: 4, top: "6%",  left: "54%", delay: "2.5s", dur: "6s"   },
];

const Hero: React.FC = () => {
const heroRef   = useRef<HTMLDivElement>(null);
  const { t, isRTL } = useLanguage();
  const th = t.home.hero;
  const [loaded,   setLoaded]   = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setMousePos({
        x: ((e.clientX - rect.left)  / rect.width  - 0.5) * 12,
        y: ((e.clientY - rect.top)   / rect.height - 0.5) * 8,
      });
    };
    window.addEventListener("mousemove", fn);
    return () => window.removeEventListener("mousemove", fn);
  }, []);

  return (
    <HeroSection ref={heroRef as any} sx={{ background: "#F0F7FF" }}>

      <Box component="img" src="/learning pet.jpg" alt="Pet parent learning how to care for their pet with Dr.Paw" aria-hidden="true"
        sx={{ display: { xs: "none", md: "block" }, position: "absolute", inset: 0, zIndex: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 35%", transform: `scale(1.04) translate(${mousePos.x * 0.18}px, ${mousePos.y * 0.12}px)`, transition: "transform 1.4s ease", animation: `${scaleReveal} 1.4s ease both` }}
      />

      {/* Overlay — opaque start side, transparent end (desktop background image only) */}
      <Box sx={{ display: { xs: "none", md: "block" }, position: "absolute", inset: 0, zIndex: 1, background: `linear-gradient(${isRTL ? "285deg" : "105deg"}, rgba(240,247,255,0.66) 0%, rgba(240,247,255,0.77) 28%, rgba(240,247,255,0.65) 50%, rgba(240,247,255,0.20) 70%, transparent 100%)` }} />
      <Box sx={{ display: { xs: "none", md: "block" }, position: "absolute", inset: 0, zIndex: 1, background: `linear-gradient(to top, rgba(21,101,192,0.05) 0%, transparent 45%)` }} />

      {particles.map((p, i) => (
        <Box key={i} sx={{ position: "absolute", top: p.top, left: p.left, zIndex: 2, width: p.size, height: p.size, borderRadius: "50%", backgroundColor: "rgba(21,101,192,0.3)", animation: `${particleFloat} ${p.dur} ease-in-out infinite`, animationDelay: p.delay, filter: "blur(0.5px)" }} />
      ))}

      <Box sx={{ position: "absolute", top: "4%", left: "38%", zIndex: 2, width: { xs: 140, md: 240 }, height: { xs: 140, md: 240 }, background: "radial-gradient(circle, rgba(21,101,192,0.07) 0%, transparent 70%)", animation: `${morphShape} 14s ease-in-out infinite`, borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%", transform: `translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.2}px)`, transition: "transform 1s ease" }} />
      <Box sx={{ position: "absolute", bottom: "14%", left: "6%", zIndex: 2, width: { xs: 90, md: 150 }, height: { xs: 90, md: 150 }, background: "radial-gradient(circle, rgba(66,165,245,0.1) 0%, transparent 70%)", animation: `${morphShape} 18s ease-in-out infinite reverse`, borderRadius: "40% 60% 70% 30% / 40% 50% 60% 50%", transform: `translate(${mousePos.x * -0.2}px, ${mousePos.y * 0.3}px)`, transition: "transform 1.2s ease" }} />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: { xs: 0, md: 4 }, py: { xs: 8, md: 10 } }}>
          <Box sx={{ maxWidth: { xs: "100%", md: "650px" }, textAlign: "center" }}>

            {/* Badge */}
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, backgroundColor: "rgba(21,101,192,0.09)", border: `1px solid rgba(21,101,192,0.2)`, borderRadius: "50px", px: 2.5, py: 1, mb: 8, backdropFilter: "blur(8px)", opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(20px)", transition: "all 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.1s", position: "relative", overflow: "hidden", "&::after": { content: '""', position: "absolute", top: 0, left: 0, width: "30%", height: "100%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)", animation: `${shimmerSlide} 3s ease-in-out infinite 2s` } }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#4CAF50", animation: `${pulse} 1.6s ease-in-out infinite`, boxShadow: "0 0 8px #4CAF50" }} />
              <Typography sx={{ color: DP.blue, fontWeight: 700, fontSize: "0.8rem", letterSpacing: isRTL ? "0.02em" : "0.1em" }}>
                {th.badge}
              </Typography>
            </Box>

            {/* Full photo shown in-flow on mobile (no crop/zoom) */}
            <Box component="img" src="/learning pet.jpg" alt="Pet parent learning how to care for their pet with Dr.Paw"
              sx={{ display: { xs: "block", md: "none" }, width: "100%", height: "auto", borderRadius: "20px", boxShadow: `0 12px 32px rgba(10,22,40,0.15)`, mb: 8, opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(20px)", transition: "all 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.05s" }}
            />

            {/* Headline */}
            <Box sx={{ mb: 10 }}>
              <Typography variant="h1" sx={{ fontSize: { xs: "2.6rem", sm: "3.4rem", md: "4rem", lg: "4.8rem" }, fontWeight: 300, color: DP.navy, lineHeight: 1.06, letterSpacing: isRTL ? "0" : "0.02em", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box sx={{ whiteSpace: "nowrap", display: "flex", gap: "0.22em" }}>
                  {th.words.map((word, i) => (
                    <Box key={i} component="span" sx={{ display: "inline-block", animation: loaded ? `${textReveal} 0.8s cubic-bezier(0.22,1,0.36,1) both` : "none", animationDelay: `${0.15 + i * 0.1}s` }}>
                      {word}
                    </Box>
                  ))}
                </Box>
                <Box component="span" sx={{ display: "inline-block", background: `linear-gradient(135deg, ${DP.blue} 0%, #42A5F5 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", animation: loaded ? `${textReveal} 0.9s cubic-bezier(0.22,1,0.36,1) both` : "none", animationDelay: "0.5s" }}>
                  {th.highlight}
                </Box>
              </Typography>
            </Box>

            {/* CTA buttons */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 10, justifyContent: "center", animation: loaded ? `${slideUp} 0.8s ease both` : "none", animationDelay: "0.75s" }}>
<AnimatedBorderButton>
                <OutlineButton onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                  sx={{ borderColor: "transparent", color: DP.navy, display: "flex", alignItems: "center", gap: 1, backgroundColor: DP.white, border: "none", "&:hover": { color: DP.blue, backgroundColor: "rgba(21,101,192,0.04)" }, transition: "all 0.3s ease" }}>
                  <PlayCircleOutlineIcon sx={{ fontSize: "1.1rem" }} />
                  {th.ctaSecondary}
                </OutlineButton>
              </AnimatedBorderButton>
            </Box>

            {/* Highlights */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 6, alignItems: "center", animation: loaded ? `${slideUp} 0.8s ease both` : "none", animationDelay: "0.82s" }}>
              {th.highlights.map((h, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: "1rem", color: DP.blue, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "0.85rem", color: DP.textMid, fontWeight: 500 }}>{h}</Typography>
                </Box>
              ))}
            </Box>

            {/* Stats row */}
            <Box sx={{ display: "flex", gap: { xs: 4, md: 5 }, pt: 8, borderTop: "1px solid rgba(10,22,40,0.1)", justifyContent: "center", animation: loaded ? `${slideUp} 0.8s ease both` : "none", animationDelay: "0.88s" }}>
              {th.stats.map((s, i) => (
                <Box key={i} sx={{ position: "relative", "&::after": i < th.stats.length - 1 ? { content: '""', position: "absolute", right: { xs: -16, md: -20 }, top: "10%", bottom: "10%", width: "1px", backgroundColor: "rgba(10,22,40,0.1)" } : {} }}>
                  <Typography sx={{ fontSize: { xs: "1.8rem", md: "2.2rem" }, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em", background: `linear-gradient(135deg, ${DP.navy}, ${DP.blue})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    {statValues[i]}
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: "rgba(10,22,40,0.48)", fontWeight: 600, letterSpacing: isRTL ? "0.02em" : "0.1em", mt: 0.4 }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Container>

      <Box sx={{ position: "absolute", bottom: -2, left: 0, right: 0, lineHeight: 0, zIndex: 5 }}>
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: "100%", height: "60px", display: "block" }}>
          <path d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z" fill="#FFFFFF" />
        </svg>
      </Box>
    </HeroSection>
  );
};

export default Hero;
